import { useId } from 'react';
import { FiGrid, FiList, FiSearch } from 'react-icons/fi';
import { cn } from '@/shared/lib';
import type { Table } from '@tanstack/react-table';
import { Select } from '@/shared/ui/select';
import type { DataTableColumn, DataTableRow } from '@/shared/ui/data-table/types';
import { dataTableStyles } from '@/shared/ui/data-table/styles';

type FilterBarProps<TData extends DataTableRow> = {
  table: Table<TData>;
  searchable: boolean;
  searchText: string;
  onSearchInput: (value: string) => void;
  /** Columns that contribute a filter control, in declaration order. */
  filterableColumns: DataTableColumn<TData>[];
  /** Current values for column-bound filters, keyed by column id. */
  columnFilters: Record<string, string>;
  setColumnFilter: (columnId: string, value: string) => void;
  hideableColumnIds: string[];
  toggleColumn: (id: string, visible: boolean) => void;
  /** Current presentation: 'table' or 'cards'. */
  presentation: 'table' | 'cards';
  /** Called when the user switches presentation. */
  onPresentationToggle: () => void;
  total: number;
};

/**
 * The second row of the table: search, column-bound filter controls and the
 * view toggle.
 *
 * Search sits on the left because the user reads left-to-right and starts by
 * typing. Column-bound filters, the Columns disclosure and the view toggle
 * sit on the right because they are query / presentation controls that the
 * user reaches for after the search box.
 *
 * The bulk bar replaces the filter bar while rows are selected, so the two
 * never stack and the active controls are never further than one row from the
 * rows they act on.
 */
export function FilterBar<TData extends DataTableRow>({
  table,
  searchable,
  searchText,
  onSearchInput,
  filterableColumns,
  columnFilters,
  setColumnFilter,
  hideableColumnIds,
  toggleColumn,
  presentation,
  onPresentationToggle,
  total,
}: FilterBarProps<TData>) {
  return (
    <div className={dataTableStyles.filterBar}>
      {searchable ? (
        <TableSearch searchText={searchText} onSearchInput={onSearchInput} />
      ) : null}

      <div className={dataTableStyles.filterBarTrailing}>
        {filterableColumns.map((column) => (
          <ColumnFilterControl
            key={column.id}
            column={column}
            value={columnFilters[column.id] ?? ''}
            onChange={(value) => setColumnFilter(column.id, value)}
            // The label is hidden below `sm` only when a search field shares the
            // row and needs the width. A page with no search box has the room,
            // and an unlabelled "Any" dropdown names nothing.
            showLabel={!searchable}
          />
        ))}

        <span className={cn(dataTableStyles.resultCount, 'hidden sm:inline')}>
          {total} {total === 1 ? 'result' : 'results'}
        </span>
        <ColumnVisibility hideableColumnIds={hideableColumnIds} table={table} toggleColumn={toggleColumn} />
        <ViewToggle presentation={presentation} onToggle={onPresentationToggle} />
      </div>
    </div>
  );
}

type TableSearchProps = {
  searchText: string;
  onSearchInput: (value: string) => void;
};

/**
 * The debounced free-text field.
 *
 * Labelled through a visually hidden `<label>` rather than a placeholder: a
 * placeholder disappears the moment the field has a value, which leaves a
 * screen reader with an unlabelled textbox on exactly the screen where a user
 * is most likely to be searching.
 *
 * The id comes from `useId`, not from the caption: a caption is display text
 * ("Tenants — 2 total") and carries spaces and an em-dash, none of which belong
 * in an `id`/`for` pair.
 */
function TableSearch({ searchText, onSearchInput }: TableSearchProps) {
  const searchId = useId();
  return (
    <div className={dataTableStyles.filterBarSearch}>
      <FiSearch size={14} aria-hidden="true" className={dataTableStyles.searchIcon} />
      <label className="sr-only" htmlFor={searchId}>
        Search
      </label>
      <input
        id={searchId}
        type="search"
        className={dataTableStyles.search}
        placeholder="Search…"
        value={searchText}
        onChange={(event) => onSearchInput(event.target.value)}
      />
    </div>
  );
}

type ColumnFilterControlProps<TData extends DataTableRow> = {
  column: DataTableColumn<TData>;
  value: string;
  onChange: (value: string) => void;
  /** Keep the label visible at every width. Set when no search field shares the row. */
  showLabel: boolean;
};

/**
 * One column-bound filter. Renders a labelled control whose value is sent to
 * the server in `DataTableParams.filters[<column.id>]`.
 *
 * The label sits beside the control, not above, so a row of filters does not
 * grow two lines tall — at three filters a vertical layout would already push
 * the filter bar past the height where the first row of the table is in view.
 */
function ColumnFilterControl<TData extends DataTableRow>({
  column,
  value,
  onChange,
  showLabel,
}: ColumnFilterControlProps<TData>) {
  // `useId` runs unconditionally so the hook order is stable whether or not
  // `column.filter` is set. The id is unused in the no-filter branch.
  const labelId = useId();
  if (!column.filter) return null;

  const labelClass = showLabel
    ? dataTableStyles.columnFilterLabelAlways
    : dataTableStyles.columnFilterLabel;

  if (column.filter.type === 'text') {
    return (
      <div className={dataTableStyles.columnFilter}>
        <label htmlFor={labelId} className={labelClass}>
          {column.header}:
        </label>
        <input
          id={labelId}
          type="search"
          value={value}
          placeholder={column.filter.placeholder ?? 'Filter…'}
          onChange={(event) => onChange(event.target.value)}
          className={dataTableStyles.columnFilterInput}
        />
      </div>
    );
  }

  return (
    <div className={dataTableStyles.columnFilter}>
      <label htmlFor={labelId} className={labelClass}>
        {column.header}:
      </label>
      <Select
        id={labelId}
        label={column.header}
        hideLabel
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // A filter that can be set but not unset is a trap, so the control always
        // offers a way back to "no filter". The empty value is what the engine reads
        // as "cleared" — `setColumnFilter` deletes the key for it.
        options={[{ value: '', label: 'All' }, ...column.filter.options]}
        placeholder={column.filter.placeholder ?? 'Any'}
        size="sm"
        className={dataTableStyles.columnFilterSelect}
      />
    </div>
  );
}

type ColumnVisibilityProps<TData extends DataTableRow> = {
  hideableColumnIds: string[];
  table: Table<TData>;
  toggleColumn: (id: string, visible: boolean) => void;
};

/** The column show/hide disclosure. Inline by design, like a chip. */
function ColumnVisibility<TData extends DataTableRow>({
  hideableColumnIds,
  table,
  toggleColumn,
}: ColumnVisibilityProps<TData>) {
  if (hideableColumnIds.length === 0) return null;

  return (
    <details className={cn(dataTableStyles.disclosure, dataTableStyles.disclosureInline)}>
      <summary className="cursor-pointer list-none">Columns</summary>
      <div className={dataTableStyles.disclosurePanel}>
        {hideableColumnIds.map((id) => (
          <ColumnToggle key={id} id={id} table={table} toggleColumn={toggleColumn} />
        ))}
      </div>
    </details>
  );
}

type ColumnToggleProps<TData extends DataTableRow> = {
  id: string;
  table: Table<TData>;
  toggleColumn: (id: string, visible: boolean) => void;
};

/** One row of the column picker. Hidden columns are still listed. */
function ColumnToggle<TData extends DataTableRow>({ id, table, toggleColumn }: ColumnToggleProps<TData>) {
  const column = table.getColumn(id);
  if (!column) return null;

  return (
    <label className={dataTableStyles.disclosureOption}>
      <input
        type="checkbox"
        checked={column.getIsVisible()}
        onChange={(event) => toggleColumn(id, event.target.checked)}
      />
      {column.id}
    </label>
  );
}

/** The presentation toggle: list view or card view. */
function ViewToggle({
  presentation,
  onToggle,
}: {
  presentation: 'table' | 'cards';
  onToggle: () => void;
}) {
  return (
    <div className={dataTableStyles.viewToggleGroup} role="group" aria-label="View">
      <button
        type="button"
        className={cn(
          dataTableStyles.viewToggle,
          presentation === 'table' ? dataTableStyles.viewToggleActive : undefined,
        )}
        aria-label="Table view"
        aria-pressed={presentation === 'table'}
        onClick={onToggle}
        disabled={presentation === 'table'}
      >
        <FiList size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={cn(
          dataTableStyles.viewToggle,
          presentation === 'cards' ? dataTableStyles.viewToggleActive : undefined,
        )}
        aria-label="Card view"
        aria-pressed={presentation === 'cards'}
        onClick={onToggle}
        disabled={presentation === 'cards'}
      >
        <FiGrid size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
